import { LucideProps } from 'lucide-react';
import React from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ConditionalRender,
  Gutter,
  Header,
  Icon,
  Inline,
  Skeleton,
  Stack,
} from '@crossnokaye/ux-common';

const isRenderFunction = (
  icon: (() => React.ReactNode) | React.ComponentType<LucideProps>
): icon is () => React.ReactNode => {
  return typeof icon === 'function' && icon.length === 0;
};

interface SectionTemplateProps {
  title: string;
  icon: (() => React.ReactNode) | React.ComponentType<LucideProps>;
  rightButtons?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
}

export const SectionTemplate = ({
  title,
  icon,
  rightButtons,
  children,
  isLoading,
}: SectionTemplateProps): React.ReactNode => {
  return (
    <ConditionalRender condition={!isLoading} fallback={<Skeletons children={children} />}>
      <Accordion type={'multiple'} defaultValue={[`sections-${title}`]} id={`sections-${title}`}>
        <AccordionItem className={'border-0'} value={`sections-${title}`}>
          <Gutter orientation="horizontal" size="responsive">
            <AccordionTrigger
              icon={
                isRenderFunction(icon) ? (
                  icon()
                ) : (
                  <Icon
                    icon={icon}
                    variant={'rounded'}
                    size={'lg'}
                    backgroundColor={'bg-secondary'}
                  />
                )
              }
              variant={'large'}
              title={title}
              rightButtons={rightButtons}
            >
              {title}
            </AccordionTrigger>
          </Gutter>
          <Gutter size="thin" orientation="horizontal" className="sm:px-3 lg:px-5">
            <AccordionContent className={'overflow-hidden mt-stack'}>
              <Stack>{children}</Stack>
            </AccordionContent>
          </Gutter>
        </AccordionItem>
      </Accordion>
    </ConditionalRender>
  );
};

const Skeletons = ({ children }: { children: React.ReactNode }) => {
  return (
    <Gutter orientation="horizontal" size="responsive">
      <Header
        title={<Skeleton className="w-40 h-9" />}
        icon={<Skeleton className="w-10 h-9" />}
        rightButtons={
          <Inline className="h-9">
            <Skeleton className="w-10 h-10" />
          </Inline>
        }
        className="mb-stack"
      />
      {children}
    </Gutter>
  );
};
